function cmrpUsedPrintVariantNoticeText() {
  if (typeof window !== 'undefined' && window.CMRP_USED_PRINT_VARIANT_NOTICE) {
    return window.CMRP_USED_PRINT_VARIANT_NOTICE;
  }
  return 'Used materials: might not include supplemental materials like access cards or CDs.';
}

function cmrpEscapeVariantNoticeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

/**
 * When a specific `.variant-notice` is shown, hide the generic digital delivery line in the same collapse.
 * @param {Element} collapseContent
 * @param {boolean} suppressed
 */
function cmrpSetDigitalDeliveryNoticeSuppressed(collapseContent, suppressed) {
  collapseContent.querySelectorAll('.digital-delivery-notice').forEach(el => {
    if (suppressed) {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('hidden');
      el.removeAttribute('aria-hidden');
    }
  });
}

/**
 * CMRP: show plain-text variant notices (last checked row with `data-variant-notice` wins)
 * as the last child of `.collapse-content`, scoped per format tab.
 * @param {Element | null | undefined} collapseContent
 */
function syncCmrpVariantNoticeInCollapse(collapseContent) {
  if (!collapseContent || typeof document === 'undefined') return;
  const grid = collapseContent.querySelector('.purchase-types');
  if (!grid) return;

  let lastMessage = '';
  grid.querySelectorAll('.purchase-checkbox:checked').forEach(cb => {
    const row = cb.closest('.purchase-type');
    const raw = row?.getAttribute('data-variant-notice');
    const msg = raw && String(raw).trim();
    if (msg) lastMessage = msg;
  });

  let slot = collapseContent.querySelector('.variant-notice');
  if (!lastMessage) {
    if (slot) slot.remove();
    cmrpSetDigitalDeliveryNoticeSuppressed(collapseContent, false);
    return;
  }

  if (!slot) {
    slot = document.createElement('div');
    slot.className = 'variant-notice';
    collapseContent.appendChild(slot);
  } else {
    collapseContent.appendChild(slot);
  }
  slot.textContent = lastMessage;
  cmrpSetDigitalDeliveryNoticeSuppressed(collapseContent, true);
}

window.syncCmrpVariantNoticeInCollapse = syncCmrpVariantNoticeInCollapse;

class MaterialInteractionManager {
  constructor(config = {}) {
    this.products = config.products || [];
    this.productHandler = config.productHandler || null;
    this.updateSelectedProductsCallback =
      config.updateSelectedProductsCallback || (() => {});
    this.updateSelectionBarSummaryCallback =
      config.updateSelectionBarSummaryCallback || (() => {});

    this._bindMethods();
    this.setupCartEventListeners();
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this._handleChoiceDrawerEscape);
    }
  }

  _getSectionScope(element) {
    const materialItem = element?.closest?.('.material-item');
    return materialItem?.closest?.('.course-section') || document;
  }

  _bindMethods() {
    const methods = [
      'handleFormatSelection',
      'updatePricingForFormat',
      'togglePurchaseOption',
      'handlePurchaseSelection',
      'addMaterialEventListeners',
      'toggleViewMore',
      'toggleViewMoreChoice',
      'populateExpandedDetails',
      'getVariantByOptions',
      'findVariantAcrossProducts',
      'getSelectedFormat',
      'handleCartUpdate',
      '_handleChoiceDrawerEscape',
      '_syncChoiceDrawerScrollLock',
      '_portalChoiceDrawerOpen',
      '_portalChoiceDrawerClose',
      '_syncChoiceRowControlFromDrawer',
      '_reconcileChoiceRowCheckedFromExpanded',
      '_updateChoiceProductPlusButton',
      '_syncChoiceMinimumAndExtraData',
    ];
    methods.forEach(method => (this[method] = this[method].bind(this)));
  }

  /**
   * Close choice variant drawer on Escape (same outcome as Hide details / backdrop).
   */
  _handleChoiceDrawerEscape(e) {
    if (e.key !== 'Escape') return;
    const open = document.querySelector(
      '.material-item.choice-info.choice-show'
    );
    if (!open) return;
    const outer =
      open.__cmrpChoiceOuter ||
      open.closest('.material-item.choice-item');
    if (!outer) return;
    const viewMoreBtn = outer.querySelector('.view-more-btn-choice');
    const checkBtn = outer.querySelector('.view-check-btn-choice');
    if (viewMoreBtn?.classList.contains('expanded')) {
      this.toggleViewMoreChoice(viewMoreBtn, 'btn');
    } else if (checkBtn?.classList.contains('expanded')) {
      this.toggleViewMoreChoice(checkBtn, 'checkbox');
    }
  }

  /** Viewports where the variant block stays in flow under the card (no portal / body scroll lock). */
  _choiceDrawerUsesInlineLayout() {
    return typeof window !== 'undefined' && window.innerWidth <= 968;
  }

  /** Lock page scroll while a choice drawer is open */
  _syncChoiceDrawerScrollLock() {
    if (typeof document === 'undefined') return;
    const open = document.querySelector(
      '.material-item.choice-info.choice-show'
    );
    const lock = !!open && !this._choiceDrawerUsesInlineLayout();
    document.body.classList.toggle('cmrp-choice-drawer-open', lock);
  }

  /**
   * Move choice details drawer to document.body (same idea as Preact createPortal
   * in suggested ProductsDrawer) so position:fixed is not clipped by CMRP ancestors.
   */
  _portalChoiceDrawerOpen(choiceInfo) {
    if (
      !choiceInfo ||
      typeof document === 'undefined' ||
      choiceInfo.dataset.cmrpChoiceDrawerPortaled === '1' ||
      this._choiceDrawerUsesInlineLayout()
    ) {
      return;
    }
    choiceInfo.__cmrpChoiceOuter =
      choiceInfo.closest('.material-item.choice-item') || null;
    choiceInfo.__cmrpPortalParent = choiceInfo.parentNode;
    choiceInfo.__cmrpPortalNext = choiceInfo.nextSibling;
    document.body.appendChild(choiceInfo);
    choiceInfo.dataset.cmrpChoiceDrawerPortaled = '1';
  }

  _portalChoiceDrawerClose(choiceInfo) {
    if (
      !choiceInfo ||
      choiceInfo.dataset.cmrpChoiceDrawerPortaled !== '1'
    ) {
      return;
    }
    const parent = choiceInfo.__cmrpPortalParent;
    const next = choiceInfo.__cmrpPortalNext;
    if (parent && parent.isConnected) {
      if (next && next.parentNode === parent) {
        parent.insertBefore(choiceInfo, next);
      } else {
        parent.appendChild(choiceInfo);
      }
    }
    delete choiceInfo.__cmrpPortalParent;
    delete choiceInfo.__cmrpPortalNext;
    delete choiceInfo.__cmrpChoiceOuter;
    delete choiceInfo.dataset.cmrpChoiceDrawerPortaled;
  }

  /**
   * Keep the row checkbox `expanded` class aligned with the real drawer visibility
   * so the next toggleViewMoreChoice flip matches user expectation.
   */
  _syncChoiceRowControlFromDrawer(productId) {
    const choiceInfo = document.getElementById(
      'choice-details-product-' + productId
    );
    const cb = document.getElementById('check-box-product-' + productId);
    if (!choiceInfo || !cb) return;
    const open = choiceInfo.classList.contains('choice-show');
    cb.classList.toggle('expanded', open);
  }

  /** Match native checkbox default: checked tracks drawer-open (expanded) for choice rows */
  _reconcileChoiceRowCheckedFromExpanded(productId) {
    const cb = document.getElementById('check-box-product-' + productId);
    if (!cb) return;
    cb.checked = cb.classList.contains('expanded');
  }

  /** Product id suffix from `check-box-product-*` or `choice-info-product-*` (handles ids with dashes). */
  _choiceProductIdFromToggleButton(button) {
    if (!button?.id) return null;
    const m = button.id.match(
      /^(?:check-box-product-|choice-info-product-)(.+)$/
    );
    return m ? m[1] : null;
  }

  /**
   * Choice card + button: show variant count inside the circle when count is positive, else +.
   * aria-expanded follows the portaled drawer.
   */
  _updateChoiceProductPlusButton(productId) {
    if (typeof document === 'undefined' || !productId) return;
    const plus = document.querySelector(
      `.choice-product-card__plus-select[data-product-id="${productId}"]`
    );
    if (!plus) return;
    const panel = document.getElementById('choice-details-product-' + productId);
    let n = 0;
    if (panel) {
      panel
        .querySelectorAll(
          `.purchase-checkbox[data-product-id="${productId}"]:checked`
        )
        .forEach(() => {
          n += 1;
        });
    }
    const icon = plus.querySelector('.choice-plus__icon');
    const countEl = plus.querySelector('.choice-plus__count');
    if (countEl) {
      if (n > 0) {
        countEl.textContent = String(n);
        countEl.removeAttribute('hidden');
      } else {
        countEl.textContent = '';
        countEl.setAttribute('hidden', '');
      }
    }
    if (icon) {
      icon.hidden = n > 0;
    }
    const open = !!panel?.classList.contains('choice-show');
    plus.setAttribute('aria-expanded', open ? 'true' : 'false');
    plus.classList.toggle('choice-product-card__plus-select--open', open);
    if (n > 0) {
      plus.setAttribute(
        'aria-label',
        `${n} variant${n === 1 ? '' : 's'} selected. ${open ? 'Close' : 'Open'} purchase options.`
      );
    } else {
      plus.setAttribute(
        'aria-label',
        open ? 'Close purchase options' : 'Open purchase options'
      );
    }
  }

  updateProducts(newProducts) {
    this.products = newProducts;
  }

  updateProductHandler(newProductHandler) {
    this.productHandler = newProductHandler;
  }

  setupCartEventListeners() {
    ['cart:add', 'cart:update', 'cart:change'].forEach(event =>
      document.addEventListener(event, this.handleCartUpdate)
    );
  }

  async handleCartUpdate() {
    try {
      await new Promise(resolve => setTimeout(resolve, 150));
      await this.updateAllInBagIndicators();
    } catch (error) {}
  }

  setCallbacks(callbacks) {
    if (callbacks.updateSelectedProducts) {
      this.updateSelectedProductsCallback = callbacks.updateSelectedProducts;
    }
    if (callbacks.updateSelectionBarSummary) {
      this.updateSelectionBarSummaryCallback =
        callbacks.updateSelectionBarSummary;
    }
  }

  handleFormatSelection(button, materialItem, product) {
    const selectedFormat = button.getAttribute('data-format');

    materialItem
      .querySelectorAll('.format-btn')
      .forEach(btn => btn.classList.remove('selected'));
    button.classList.add('selected');

    const priceBlocks = materialItem.querySelector('.price-blocks');
    if (priceBlocks) priceBlocks.style.display = 'flex';

    const deliveryNotice = materialItem.querySelector(
      '.delivery-notice[data-format-specific="digital"]'
    );
    if (deliveryNotice) {
      deliveryNotice.style.display =
        selectedFormat === 'digital' ? 'block' : 'none';
    }

    if (selectedFormat) {
      this.updatePricingForFormat(materialItem, selectedFormat, product);
    }
  }

  updatePricingForFormat(materialItem, format, product) {
    const purchaseTypes = materialItem.querySelector('.purchase-types');
    if (!purchaseTypes) return;

    const courseCard = materialItem.closest('.course-card');
    const sectionData = {
      courseInfo: {
        selectedsectionId: courseCard?.dataset?.selectedSectionId || '',
      },
    };

    purchaseTypes.innerHTML = '';
    let hasAvailableOptions = false;
    const variantsByType = {};

    const filteredVariants =
      product.variants?.filter(variant =>
        variant.option1?.toLowerCase().includes(format.toLowerCase())
      ) || [];

    filteredVariants.forEach(variant => {
      const type = variant.option2 || 'Buy';
      if (!variantsByType[type]) variantsByType[type] = [];
      variantsByType[type].push(variant);
    });

    Object.keys(variantsByType).forEach(type => {
      const variant = variantsByType[type][0];
      if (!variant) return;

      const price = parseFloat(variant.price).toFixed(2);
      const productId = product.title.replace(/\s+/g, '-').toLowerCase();
      const typeId = type.toLowerCase().replace(/\s+/g, '-');
      const isAvailable = variant.available;

      const mfa = Array.isArray(variant?.metafields) ? variant.metafields : [];
      const bookISBN = mfa.find(metafield => metafield.isbn_ean)?.isbn_ean || '';
      const bookEdition = mfa.find(metafield => metafield.edition)?.edition || '';
      const bookCopyright = mfa.find(metafield => metafield.copyright_year)?.copyright_year || '';
      const bookPublisher = mfa.find(metafield => metafield.publisher_name)?.publisher_name || '';
      const bookRentDueDate = mfa.find(metafield => metafield.expiration_date)?.expiration_date || '';


      const taxonomy_dep = mfa.find(metafield => 
          metafield.department && metafield.namespace === "taxonomy"
      )?.department || '';

      const taxonomy_sub_dep = mfa.find(metafield => 
          metafield.sub_department && metafield.namespace === "taxonomy"
      )?.sub_department || '';

      const taxonomy_class = mfa.find(metafield => 
          metafield.class && metafield.namespace === "taxonomy"
      )?.class || '';

      const taxonomy_sub_class = mfa.find(metafield => 
          metafield.sub_class && metafield.namespace === "taxonomy"
      )?.sub_class || '';

      const rental_only_sku = mfa.find(metafield => 
          metafield.rental_only_sku && metafield.namespace === "cm_rental"
      )?.rental_only_sku || '';

      if (isAvailable) hasAvailableOptions = true;

      const availabilityClass = isAvailable ? '' : ' unavailable';
      const inBagHTML = window.generateInBagIndicatorHTML?.() || '';

      const subscriptionLength = mfa.find(metafield => metafield.subscription)?.subscription || '';

      const sectionId = materialItem?.dataset?.purchaseInstanceId || '';
      purchaseTypes.innerHTML += this._generatePurchaseTypeHTML(
        type,
        typeId,
        productId,
        variant,
        price,
        availabilityClass,
        inBagHTML,
        product,
        bookISBN,
        bookEdition,
        bookCopyright,
        bookPublisher,
        bookRentDueDate,
        taxonomy_dep,
        taxonomy_sub_dep,
        taxonomy_class,
        taxonomy_sub_class,
        rental_only_sku,
        subscriptionLength,
        sectionId,
        sectionData
      );
    });

    if (!hasAvailableOptions && Object.keys(variantsByType).length === 0) {
      purchaseTypes.innerHTML = `
        <div class="no-options-message" style="text-align: center; padding: 20px; color: #666;">
          <p>No purchase options available for this format, yet.</p>
          <p style="font-size: 0.875rem; margin-top: 8px;">Please check back in later.</p>
        </div>
      `;
    }

    this._updateNotifyMeVisibility(
      materialItem,
      hasAvailableOptions,
      format,
      product
    );
    this._updateInBagIndicators(materialItem);

    const collapseContent = purchaseTypes.closest('.collapse-content');
    if (collapseContent) {
      syncCmrpVariantNoticeInCollapse(collapseContent);
    }
  }

  _generatePurchaseTypeHTML(
    type,
    typeId,
    productId,
    variant,
    price,
    availabilityClass,
    inBagHTML,
    product,
    bookISBN,
    bookEdition,
    bookCopyright,
    bookPublisher,
    bookRentDueDate,
    taxonomy_dep,
    taxonomy_sub_dep,
    taxonomy_class,
    taxonomy_sub_class,
    rental_only_sku,
    subscriptionLength,
    sectionId = '',
    sectionData = { courseInfo: {} }
  ) {
    const formatLower = String(variant?.option1 || '').toLowerCase();
    const typeLower = type.toLowerCase();
    const printVariantNoticeEligible =
      formatLower === 'print' &&
      (typeLower.includes('buy used') ||
        typeLower.includes('rent used') ||
        (typeLower.includes('used') &&
          (typeLower.includes('buy') || typeLower.includes('rent'))));
    const variantNoticeAttr = printVariantNoticeEligible
      ? ` data-variant-notice="${cmrpEscapeVariantNoticeAttr(cmrpUsedPrintVariantNoticeText())}"`
      : '';

    const isDigitalStr =
      window.ProductSystem?.getVariantIsDigitalString?.(variant, product) || '';
    const publisher_code =
      window.ProductSystem?.getVariantPublisherCode?.(variant, product) || '';

    const variantValueId = typeof variant.id === 'string' ? variant.id.split('/').pop() : variant.id;
    const sectionSuffix = sectionId ? String(sectionId).replace(/[^a-zA-Z0-9-]/g, '_') : '';
    const checkboxId = sectionSuffix
      ? `${typeId}-${productId}-${variantValueId}-${sectionSuffix}`
      : `${typeId}-${productId}-${variantValueId}`;

    let hidePurchaseSalesChannel = false;
    if (
      formatLower.includes('digital') &&
      window.ProductSystem?.courseMaterialsShouldHideDigitalPurchase
    ) {
      hidePurchaseSalesChannel = window.ProductSystem.courseMaterialsShouldHideDigitalPurchase(
        product,
        sectionData,
        variant,
        isDigitalStr
      );
    }
    const discoverViewNoSelection =
      window.ProductSystem?.isFollettDiscoverViewPage?.() === true;
    // OOS rows still show the checkmark UI; the input is disabled when unavailable.
    const showCheckbox =
      !discoverViewNoSelection && !hidePurchaseSalesChannel;
    const showPrice = !hidePurchaseSalesChannel;
    const isUnavailable = availabilityClass.includes('unavailable');
    const hideOosNotifyOnCmrp =
      document.body?.classList?.contains('course-materials-page') === true;
    const oosNotifyClick =
      isUnavailable && !hideOosNotifyOnCmrp
        ? `onclick='event.stopPropagation(); window.handleNotifyMe(${JSON.stringify(variant.id)}, ${JSON.stringify(product.title)}, ${JSON.stringify(type)});'`
        : '';
    const oosExtra = isUnavailable
      ? hideOosNotifyOnCmrp
        ? `<div class="purchase-type__oos-tooltip" role="tooltip">This item is out of stock.</div>`
        : `
        <div class="purchase-type__oos-tooltip" role="tooltip">This item is out of stock.</div>
        <div class="purchase-type__oos-foot">
          <button type="button" class="purchase-type__notify-btn" ${oosNotifyClick}>
            <svg class="purchase-type__notify-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <path d="M3 7l9 6 9-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Notify Me</span>
          </button>
        </div>`
      : '';
    return `
      <div class="purchase-type${availabilityClass}"${variantNoticeAttr}
           data-purchase-type="${typeId}" 
           data-variant-id="${variant.id}"
           onclick="window.togglePurchaseOption(this, event)">
        <div class="purchase-type-header">
          <span class="purchase-type-label">${type.toUpperCase()}</span>
          <div class="purchase-price">$${price}</div>
          ${
            showCheckbox
              ? `<div class="purchase-checkbox-container" onclick="event.stopPropagation()">
            <input type="checkbox" 
                   id="${checkboxId}" 
                   class="purchase-checkbox" 
                   data-format="${variant.option1}"
                   data-type="${type}"
                   data-product-id="${variant.parentProductId.split('/').pop()}"
                   data-purchase-type="${typeId}"
                   data-variant-id="${variant.id}"
                   data-variant-price="${price}"
                   data-book-isbn="${bookISBN}"
                   data-book-edition="${bookEdition}"
                   data-book-copyright="${bookCopyright}"
                   data-book-publisher="${bookPublisher}"
                   data-book-rent-due-date="${bookRentDueDate}"
                   data-department-taxonomy="${taxonomy_dep}"
                   data-sub-department="${taxonomy_sub_dep}"
                   data-class="${taxonomy_class}"
                   data-sub-class="${taxonomy_sub_class}"
                   data-product-title="${product.title.replace(/'/g, "\\'")}"
                   data-rental-only-sku="${rental_only_sku}"
                   data-subscription-length="${subscriptionLength}"
                   data-section-id="${sectionId}"
                   data-is-digital="${String(isDigitalStr).toLowerCase() === 'true' ? 'true' : 'false'}"
                   data-publisher-code="${publisher_code}"
                   onchange="window.handlePurchaseSelection(this)"${isUnavailable ? ' disabled' : ''}>
            <label for="${checkboxId}" class="checkbox-label"></label>
          </div>`
              : ''
          }
        </div>
        ${inBagHTML}
        ${oosExtra}
      </div>
    `;
  }

  _updateNotifyMeVisibility(
    materialItem,
    hasAvailableOptions,
    format,
    product
  ) {
    const notifyContainer = materialItem.querySelector(
      '.material-notify-container'
    );
    if (!notifyContainer) return;

    notifyContainer.style.display = 'none';
    notifyContainer.innerHTML = '';

    const attributes = {
      'data-product-id': product.id,
      'data-product-title': product.title,
      'data-format': format,
      'data-has-available-options': hasAvailableOptions.toString(),
    };

    Object.entries(attributes).forEach(([key, value]) => {
      notifyContainer.setAttribute(key, value);
    });
  }

  _checkSimpleProductNotifyMe(materialItem, product) {
    const purchaseOptions = materialItem.querySelectorAll(
      '.purchase-type:not(.unavailable)'
    );
    const hasAvailableOptions = purchaseOptions.length > 0;
    this._updateNotifyMeVisibility(
      materialItem,
      hasAvailableOptions,
      'simple',
      product
    );
  }

  togglePurchaseOption(purchaseTypeDiv, event) {
    if (event?.target && event.target.closest('.purchase-checkbox-container'))
      return;
    if (event?.target?.closest?.('.requirements-link')) return;

    if (purchaseTypeDiv.classList.contains('unavailable')) {
      if (event?.target?.closest?.('.purchase-type__notify-btn')) return;
      if (
        event?.target?.closest?.(
          'a, button, .requirements-link, anchored-popover-component',
        )
      ) {
        return;
      }
      purchaseTypeDiv.classList.toggle('is-oos-tooltip-visible');
      return;
    }

    const checkbox = purchaseTypeDiv.querySelector('.purchase-checkbox');
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change'));
    }
  }
  handleMobileSelection(plusboxSelection, productID) {
    if (!plusboxSelection) return;
    if (plusboxSelection.classList.contains('choice-product-card__plus-select')) {
      this._updateChoiceProductPlusButton(productID);
      return;
    }
    const materialItem = plusboxSelection.closest('.material-item');
    const elementsCheck = materialItem
      ? materialItem.querySelectorAll(`[data-product-id="${productID}"]`)
      : [];
    let selectedCount = 0;
    elementsCheck.forEach(element => {
      if (element.selected || element.checked) selectedCount++;
    });
    plusboxSelection.innerHTML = selectedCount > 0
      ? String(selectedCount)
      : `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  updateViewMoreDetailsChoice(productID, sectionScope){
    const scope = sectionScope || document;
    const buttonViewCheck = scope.querySelector("#choice-info-product-"+productID);
    if (!buttonViewCheck) return;
    const text = 'View details';
    const rotation = '0deg';
    const spanElement = buttonViewCheck.querySelector('span');
    if (spanElement) spanElement.textContent = text;
    const chevron = buttonViewCheck.querySelector('.chevron-down');
    if (chevron) chevron.style.transform = `rotate(${rotation})`;

    if (buttonViewCheck.classList.contains("expanded")) {
      buttonViewCheck.classList.remove("expanded");
    }
    this._updateChoiceProductPlusButton(productID);
  }
  hideShowPriceChoice(range,price,productID,sectionScope){
    const scope = sectionScope || document;
    if(range){
      const rangeChoice = scope.querySelector('#price-range-choice-'+productID);
      if (rangeChoice) {
        rangeChoice.classList.add("show-price-choice");
        rangeChoice.classList.remove("hide-price-choice");
      }
      const priceChoice = scope.querySelector('#price-choice-'+productID);
      if (priceChoice) {
        priceChoice.classList.add("hide-price-choice");
        priceChoice.classList.remove("show-price-choice");
      }
    }else{
      const priceChoice = scope.querySelector('#price-choice-'+productID);
      if (priceChoice) {
        priceChoice.classList.add("show-price-choice");
        priceChoice.classList.remove("hide-price-choice");
        priceChoice.innerText = "$"+price;
      }
      const rangeChoice = scope.querySelector('#price-range-choice-'+productID);
      if (rangeChoice) {
        rangeChoice.classList.add("hide-price-choice");
        rangeChoice.classList.remove("show-price-choice");
      }
    }
  }

  /**
   * Choice section: grid swap, completed banner, and choice-extra-data from current DOM selections.
   * Inline mobile: also counts products with any checked variant in the panel when the row
   * purchase-choice checkbox is unchecked (e.g. after closing the variant panel).
   */
  _syncChoiceMinimumAndExtraData(sectionID) {
    if (!sectionID || typeof document === 'undefined') return;

    const selectedChoices = [];
    const selectedPrices = [];
    const checkboxesChoice = document.querySelectorAll('.purchase-choice-checkbox');

    checkboxesChoice.forEach(cb => {
      if (cb.checked) {
        const productID = cb.id.split('check-box-product-')[1];
        const choiceSectionScope = this._getSectionScope(cb);
        const relatedCheckboxes = choiceSectionScope.querySelectorAll(
          `[data-product-id="${productID}"]`
        );
        let hasChecked = false;
        relatedCheckboxes.forEach(relatedCheckbox => {
          if (relatedCheckbox.checked) {
            hasChecked = true;
            const price = relatedCheckbox.getAttribute('data-variant-price');
            if (price) selectedPrices.push(price);
          }
        });
        if (hasChecked) {
          selectedChoices.push(productID);
        }
      }
    });

    if (this._choiceDrawerUsesInlineLayout()) {
      const grid = document.getElementById('choice-items-' + sectionID);
      grid?.querySelectorAll('.choice-product-card__plus-select').forEach(plus => {
        const pid = plus.getAttribute('data-product-id');
        if (!pid || selectedChoices.includes(pid)) return;
        const pan = document.getElementById('choice-details-product-' + pid);
        if (!pan) return;
        const variantCbs = pan.querySelectorAll(
          `.purchase-checkbox[data-product-id="${pid}"]:checked`
        );
        if (variantCbs.length === 0) return;
        selectedChoices.push(pid);
        variantCbs.forEach(cb => {
          const price = cb.getAttribute('data-variant-price');
          if (price) selectedPrices.push(price);
        });
      });
    }

    const choice_minimum = document.getElementById('choice-minimum-' + sectionID);

    let dataProducts;
    let idProductFilt = 0;
    if (window.shopifySettings && window.shopifySettings.unifiedViewCMRP) {
      dataProducts = window.courseData.products_Materials.reduce((acc, product) => {
        if (product.metafields.book_type_ind === 'CEB') {
          const printRelationshipISBN = product.metafields.print_relationship_isbn;
          const isbn_ean = product.metafields.isbn_ean;
          if (printRelationshipISBN && printRelationshipISBN !== '') {
            const isbnArray = JSON.parse(printRelationshipISBN);
            const shouldRemove = isbnArray.some(isbn => {
              return window.courseData.products_Materials.some(
                item =>
                  item.metafields.isbn_ean === isbn &&
                  item.material_type === product.material_type
              );
            });
            if (shouldRemove) {
              const variantsToAdd = product.variants || [];
              const parentProduct = window.courseData.products_Materials.find(
                item =>
                  item.metafields.book_type_ind === 'TXT' &&
                  item.material_type === product.material_type &&
                  isbnArray.some(isbn => item.metafields.isbn_ean === isbn)
              );
              if (parentProduct) {
                parentProduct.variants = [
                  ...(parentProduct.variants || []),
                  ...variantsToAdd,
                ];
              }
              return acc;
            }
          }

          if (isbn_ean && isbn_ean !== '') {
            const shouldRemove = window.courseData.products_Materials.some(
              item =>
                item.metafields.isbn_ean === isbn_ean &&
                item.material_type === product.material_type
            );
            if (shouldRemove && idProductFilt > 0) {
              const variantsToAdd = product.variants || [];
              const parentProduct = window.courseData.products_Materials.find(
                item =>
                  item.metafields.book_type_ind === 'CEB' &&
                  item.material_type === product.material_type &&
                  item.metafields.isbn_ean === isbn_ean
              );
              if (parentProduct) {
                parentProduct.variants = [
                  ...(parentProduct.variants || []),
                  ...variantsToAdd,
                ];
              }
              idProductFilt++;
              return acc;
            }
            idProductFilt++;
          }
        }
        acc.push(product);
        return acc;
      }, []);
    } else {
      dataProducts = window.courseData.products_Materials;
    }

    const filteredProductsChoiceSelected = dataProducts.filter(
      product =>
        selectedChoices.some(choice => choice === product.id) &&
        product.material_type === 'Choice' &&
        product.sectionId.includes(sectionID)
    );

    if (!choice_minimum) return;

    if (selectedChoices.length >= parseInt(choice_minimum.value, 10)) {
      if (typeof window.updateChoiceProducts === 'function') {
        window.updateChoiceProducts(filteredProductsChoiceSelected);
      }
      if (typeof window.updatePriceData === 'function') {
        window.updatePriceData(selectedPrices);
      }
      const choiceItems = document.getElementById('choice-items-' + sectionID);
      const choiceFinalItems = document.getElementById('choice-final-items-' + sectionID);

      /* Desktop only: swap grid for final picker. Inline (≤968px) keeps the product grid visible. */
      if (!this._choiceDrawerUsesInlineLayout()) {
        choiceItems?.classList.add('hide-choice-items');
        choiceItems?.classList.remove('show-choice-items');

        choiceFinalItems?.classList.add('show-choice-items');
        choiceFinalItems?.classList.remove('hide-choice-items');
      }

      if (selectedChoices.length === parseInt(choice_minimum.value, 10)) {
        const choiceCompleted = document.getElementById(
          'choice-completed-' + sectionID
        );
        choiceCompleted?.classList.add('show-choice-completed');
        choiceCompleted?.classList.remove('hide-choice-completed');

        const choiceExtraData = document.getElementById(
          'choice-extra-data-' + sectionID
        );
        choiceExtraData?.classList.remove('show-choice-extra-data');
        choiceExtraData?.classList.add('hide-choice-extra-data');
      } else {
        const choiceCompleted = document.getElementById(
          'choice-completed-' + sectionID
        );
        choiceCompleted?.classList.remove('show-choice-completed');
        choiceCompleted?.classList.add('hide-choice-completed');

        const choiceExtraData = document.getElementById(
          'choice-extra-data-' + sectionID
        );
        choiceExtraData?.classList.add('show-choice-extra-data');
        choiceExtraData?.classList.remove('hide-choice-extra-data');
      }
    } else {
      const choiceItems = document.getElementById('choice-items-' + sectionID);
      const choiceFinalItems = document.getElementById('choice-final-items-' + sectionID);

      if (!this._choiceDrawerUsesInlineLayout()) {
        choiceItems?.classList.remove('hide-choice-items');
        choiceItems?.classList.add('show-choice-items');

        choiceFinalItems?.classList.remove('show-choice-items');
        choiceFinalItems?.classList.add('hide-choice-items');
      }

      const choiceCompleted = document.getElementById(
        'choice-completed-' + sectionID
      );
      choiceCompleted?.classList.remove('show-choice-completed');
      choiceCompleted?.classList.add('hide-choice-completed');

      const choiceExtraData = document.getElementById(
        'choice-extra-data-' + sectionID
      );
      choiceExtraData?.classList.remove('show-choice-extra-data');
      choiceExtraData?.classList.add('hide-choice-extra-data');
    }
  }

  handlePurchaseSelection(checkbox) {
    const materialItem = checkbox.closest('.material-item');
    if (!materialItem) return;
    const formatBtns = materialItem.querySelectorAll('.format-btn');
    if (formatBtns.length > 0 && !this.getSelectedFormat(materialItem)) {
      alert('Please select a format (Digital or Print) first.');
      checkbox.checked = false;
      return;
    }
    const isMobile = window.innerWidth <= 786;
      
    const productID = checkbox.getAttribute('data-product-id');
    const sectionID = checkbox.getAttribute('data-section-id');
    const price = checkbox.getAttribute('data-variant-price');
    const sectionScope = this._getSectionScope(checkbox);
    const checkboxChoice = sectionScope.querySelector("#check-box-product-"+productID);
    const plusboxSelection = materialItem.querySelector(
      '.choice-product-card__plus-select, .material-image-plus-button'
    );
    const divDetails = sectionScope.querySelector("#div-view-details-"+productID);
    const choiceInfo = document.getElementById(
      'choice-details-product-' + productID
    );

    // Non-react CMRP cards now allow only one selected variant per product.
    // If a variant is checked, clear any other checked variant for the same product
    // (including variants rendered under a different format tab).
    if (checkbox.checked) {
      // Enforce a single selected variant per rendered product card.
      // Scope to the current material item so this works even when variant rows
      // across tabs carry different product IDs (e.g. merged/unified data).
      const otherCheckedInCard = materialItem.querySelectorAll(
        '.purchase-checkbox:checked'
      );
      otherCheckedInCard.forEach(otherCheckbox => {
        if (otherCheckbox === checkbox) return;
        otherCheckbox.checked = false;
      });
    }
    if(divDetails && !isMobile){
      if(checkbox.checked){
        divDetails.classList.remove("material-view-more-choice");
        if (choiceInfo) {
          choiceInfo.classList.add("choice-hide");
          choiceInfo.classList.remove("choice-show");
          this._portalChoiceDrawerClose(choiceInfo);
        }
        this.updateViewMoreDetailsChoice(productID, sectionScope);


        const elementsCheck = sectionScope.querySelectorAll(`[data-product-id="${productID}"]`);
        let selectedCount = 0; 
        elementsCheck.forEach(element => {           
          if (element.checked) { 
            selectedCount++; 
          }
        });
        
        const isAnySelected = selectedCount >= 2; 
        this.hideShowPriceChoice(isAnySelected,price,productID,sectionScope);

      }else{
        if(checkboxChoice){
          const elementsCheck = sectionScope.querySelectorAll(`[data-product-id="${productID}"]`);
          let isAnySelected = false;
          elementsCheck.forEach(element => {           
            if (element.selected || element.checked) { 
              isAnySelected = true; 
            }
          });
          if(!isAnySelected){
            divDetails.classList.add("material-view-more-choice");
          }else{
            divDetails.classList.remove("material-view-more-choice");
            if (choiceInfo) {
              choiceInfo.classList.add("choice-hide");
              choiceInfo.classList.remove("choice-show");
              this._portalChoiceDrawerClose(choiceInfo);
            }
            this.updateViewMoreDetailsChoice(productID, sectionScope);
          }
          
        }

        const elementsCheck = sectionScope.querySelectorAll(`[data-product-id="${productID}"]`);
        let selectedCount = 0;
        let priceProductSelected = '' 
        elementsCheck.forEach(element => {           
          if (element.checked) { 
            selectedCount++; 
            priceProductSelected = element.getAttribute('data-variant-price');
          }
        });
        
        const isAnySelected = selectedCount >= 2;
        this.hideShowPriceChoice(isAnySelected,priceProductSelected,productID,sectionScope);
      }

    }
    
    
    if(plusboxSelection && isMobile){
      this.handleMobileSelection(plusboxSelection,productID);

      // const button = plusboxSelection;
      
      // if (button) {
      //   const materialItem = button.closest('.material-item');
      //   if (!materialItem) return;
        
      //   const purchaseOptions = materialItem.querySelector('.material-purchase-options');
      //   if (!purchaseOptions) return;
        
      //   purchaseOptions.classList.remove('show');
      //   button.classList.remove('expanded');
      // }

      // Keep the variant container open when selecting a variant on mobile
      // (no longer auto-collapse after selection)

    }
    
    

    const purchaseTypeDiv = checkbox.closest('.purchase-type');
    const isUnavailable = purchaseTypeDiv?.classList.contains('unavailable');

    this._updateNotifyMeButton(
      materialItem,
      isUnavailable && checkbox.checked ? checkbox : undefined
    );

    if (!isUnavailable) {
      this.updateSelectedProductsCallback();
      this.updateSelectionBarSummaryCallback();
    }

    if (
      divDetails &&
      sectionID &&
      document.getElementById('choice-minimum-' + sectionID)
    ) {
      this._syncChoiceMinimumAndExtraData(sectionID);
    }

    const collapseForNotice = checkbox.closest('.collapse-content');
    if (collapseForNotice) {
      syncCmrpVariantNoticeInCollapse(collapseForNotice);
    }
    if (checkbox.checked) {
      materialItem.querySelectorAll('.purchase-checkbox').forEach(cb => {
        const collapse = cb.closest('.collapse-content');
        if (collapse) {
          syncCmrpVariantNoticeInCollapse(collapse);
        }
      });
    }

    if (productID) {
      this._updateChoiceProductPlusButton(productID);
    }

    this._syncChoiceDrawerScrollLock();
  }

  _updateNotifyMeButton(materialItem, selectedCheckbox = undefined) {
    const notifyContainer = materialItem.querySelector(
      '.material-notify-container'
    );
    if (!notifyContainer) return;

    if (document.body?.classList?.contains('course-materials-page')) {
      notifyContainer.style.display = 'none';
      notifyContainer.innerHTML = '';
      return;
    }

    const selectedUnavailableItems = materialItem.querySelectorAll(
      '.purchase-type.unavailable .purchase-checkbox:checked'
    );

    if (selectedUnavailableItems.length === 0) {
      notifyContainer.style.display = 'none';
      notifyContainer.innerHTML = '';
      return;
    }

    const contextCheckbox =
      selectedCheckbox ||
      selectedUnavailableItems[selectedUnavailableItems.length - 1];
    if (!contextCheckbox) return;

    const productTitle = notifyContainer.getAttribute('data-product-title');
    const format = notifyContainer.getAttribute('data-format');
    const variantId = contextCheckbox.getAttribute('data-variant-id');

    notifyContainer.style.display = 'block';
    notifyContainer.innerHTML = `
      <div class="notify-me-btn" onclick="handleNotifyMe('${variantId}', '${productTitle}', '${format}')">
        <svg class="notify-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 4v4M8 12h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="notify-text"><span class="underlined-text">Notify me</span> when it's back in stock.</span>
      </div>
    `;
  }

  addMaterialEventListeners(materialItem, product) {
    const viewMoreBtn = materialItem.querySelector('.view-more-btn');
    if (viewMoreBtn) {
      viewMoreBtn.addEventListener('click', () =>
        this.toggleViewMore(viewMoreBtn)
      );
    }

    const viewMoreBtnChoice = materialItem.querySelector('.view-more-btn-choice');
    if (viewMoreBtnChoice) {
      viewMoreBtnChoice.addEventListener('click', (e) => {
        this.toggleViewMoreChoice(viewMoreBtnChoice,'btn')
      });
    }

    const choiceDetailsPanel = materialItem.querySelector(
      '[id^="choice-details-product-"]'
    );
    if (choiceDetailsPanel) {
      const backdrop = choiceDetailsPanel.querySelector('.choice-drawer-backdrop');
      const closeBtn = choiceDetailsPanel.querySelector('.choice-drawer-close');
      const collapseOpenChoiceDrawer = () => {
        const viewMoreBtn = materialItem.querySelector('.view-more-btn-choice');
        const checkBtn = materialItem.querySelector('.view-check-btn-choice');
        if (viewMoreBtn?.classList.contains('expanded')) {
          this.toggleViewMoreChoice(viewMoreBtn, 'btn');
        } else if (checkBtn?.classList.contains('expanded')) {
          this.toggleViewMoreChoice(checkBtn, 'checkbox');
        }
      };
      if (backdrop) {
        backdrop.addEventListener('click', () => collapseOpenChoiceDrawer());
      }
      if (closeBtn) {
        closeBtn.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          collapseOpenChoiceDrawer();
        });
      }
    }

    const viewCheckBtnChoice = materialItem.querySelector('.view-check-btn-choice');
    if (viewCheckBtnChoice) {
      viewCheckBtnChoice.addEventListener('click', () => {
        this.toggleViewMoreChoice(viewCheckBtnChoice, 'checkbox');
      });
    }

    const choicePlus = materialItem.querySelector(
      '.choice-product-card__plus-select'
    );
    if (choicePlus) {
      const pid = choicePlus.getAttribute('data-product-id');
      const openDrawer = () => {
        this._syncChoiceRowControlFromDrawer(pid);
        const checkBtn = document.getElementById('check-box-product-' + pid);
        if (checkBtn) {
          this.toggleViewMoreChoice(checkBtn, 'checkbox');
        }
      };
      choicePlus.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
      });
      choicePlus.addEventListener('keydown', e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        openDrawer();
      });
      this._updateChoiceProductPlusButton(pid);
    }

    // Add collapse toggle functionality
    const collapseHeaders = materialItem.querySelectorAll('.collapse-header');
    collapseHeaders.forEach(header => {
      header.addEventListener('click', () =>
        this.toggleFormatCollapse(header)
      );
    });

    // Apply Smart Pick labels to all open collapses on page load
    const openCollapses = materialItem.querySelectorAll('.format-collapse.active');
    openCollapses.forEach(collapse => {
      //this.applySmartPickLabels(collapse);
    });

    const formatBtns = materialItem.querySelectorAll('.format-btn');
    formatBtns.forEach(btn => {
      btn.addEventListener('click', () =>
        this.handleFormatSelection(btn, materialItem, product)
      );
    });

    if (formatBtns.length === 1) {
      this._handleSingleFormat(materialItem, formatBtns[0], product);
    }

    if (formatBtns.length === 0) {
      this._checkSimpleProductNotifyMe(materialItem, product);
      this._updateInBagIndicators(materialItem);
    }

    const purchaseCheckboxes =
      materialItem.querySelectorAll('.purchase-checkbox');
    purchaseCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () =>
        this.handlePurchaseSelection(checkbox)
      );
    });
  }

  /**
   * Toggle format collapse visibility
   * @param {HTMLElement} header - The collapse header element
   */
  toggleFormatCollapse(button) {
    const container = button.closest('.purchase-options-container');
    if (!container) return;

    const format = button.dataset.format;
    const isActive = button.classList.contains('active');
    
    // If clicking on an already active tab, do nothing
    if (isActive) return;
    
    // Update tab states
    const allTabs = container.querySelectorAll('.format-tab');
    allTabs.forEach(tab => {
      tab.classList.remove('active');
    });
    button.classList.add('active');
    
    // Update collapse states
    const allCollapses = container.querySelectorAll('.format-collapse');
    allCollapses.forEach(c => {
      c.classList.remove('active');
    });
    
    // Show the selected format collapse
    const targetCollapse = container.querySelector(`.format-collapse[data-format="${format}"]`);
    if (targetCollapse) {
      targetCollapse.classList.add('active');
      //this.applySmartPickLabels(targetCollapse);
      const targetContent = targetCollapse.querySelector('.collapse-content');
      if (targetContent) {
        syncCmrpVariantNoticeInCollapse(targetContent);
      }
    } else {
      console.error('targetCollapse not found for format:', format);
    }
  }

  /**
   * Apply Smart Pick labels to the lowest price available option in each format
   * @param {HTMLElement} collapse - The format collapse element
   */
  applySmartPickLabels(collapse) {
    const purchaseTypes = collapse.querySelectorAll('.purchase-type:not(.unavailable)');
    if (purchaseTypes.length === 0) return;

    // Remove existing smart pick labels and classes
    purchaseTypes.forEach(type => {
      const existingLabel = type.querySelector('.smart-pick-label');
      if (existingLabel) {
        existingLabel.remove();
      }
      type.classList.remove('smart-pick');
    });

    // Find the lowest price option
    let lowestPrice = Infinity;
    let lowestPriceOption = null;

    purchaseTypes.forEach(type => {
      // Try multiple price selectors
      const priceElement = type.querySelector('.price') || 
                          type.querySelector('.purchase-price') ||
                          type.querySelector('[class*="price"]');
      
      if (priceElement) {
        const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
        const price = parseFloat(priceText);
        
        if (!isNaN(price) && price < lowestPrice) {
          lowestPrice = price;
          lowestPriceOption = type;
        }
      }
    });


    // Add Smart Pick label to the lowest price option
    if (lowestPriceOption) {
      const smartPickLabel = document.createElement('div');
      smartPickLabel.className = 'smart-pick-label';
      smartPickLabel.textContent = 'Smart Pick';
      
      // Make the purchase type container relative positioned and add smart-pick class
      lowestPriceOption.style.position = 'relative';
      lowestPriceOption.classList.add('smart-pick');
      lowestPriceOption.appendChild(smartPickLabel);
    }
  }

  _handleSingleFormat(materialItem, formatBtn, product) {
    formatBtn.classList.add('selected');

    const priceBlocks = materialItem.querySelector('.price-blocks');
    if (priceBlocks) priceBlocks.style.display = 'flex';

    const format = formatBtn.getAttribute('data-format');
    if (format) this.updatePricingForFormat(materialItem, format, product);
  }

  toggleViewMore(button) {
    const materialItem = button.closest('.material-item');
    if (!materialItem) return;

    let detailsSection = materialItem.querySelector(
      '.material-details-expanded'
    );
    if (!detailsSection) {
      detailsSection = this._createDetailsSection(materialItem);
    }

    const isExpanded = button.classList.contains('expanded');
    this._toggleExpandedState(button, detailsSection, isExpanded, materialItem);
  }
  toggleViewMoreChoice(button,type) {
    const materialItem = button.closest('.material-item');
    if (!materialItem) return;

    let detailsSection = materialItem.querySelector(
      '.material-details-expanded'
    );
    if (!detailsSection) {
      detailsSection = this._createDetailsSection(materialItem);
    }

    const isExpanded = button.classList.contains('expanded');
    if (type == 'btn') {
      this._toggleExpandedStateChoice(
        button,
        detailsSection,
        isExpanded,
        materialItem
      );
    }
    if (type == 'checkbox') {
      this._toggleExpandedStateChoiceCheck(
        button,
        detailsSection,
        isExpanded,
        materialItem
      );
    }

    const pid = this._choiceProductIdFromToggleButton(button);
    if (pid) {
      requestAnimationFrame(() => {
        if (type === 'checkbox') {
          this._reconcileChoiceRowCheckedFromExpanded(pid);
        }
        this._updateChoiceProductPlusButton(pid);
      });
    }
  }

  _createDetailsSection(materialItem) {
    const detailsSection = document.createElement('div');
    detailsSection.className = 'material-details-expanded';

    const targets = ['.material-details', '.material-text'];
    const parent =
      targets.map(sel => materialItem.querySelector(sel)).find(el => el) ||
      materialItem;
    parent.appendChild(detailsSection);

    return detailsSection;
  }

  _toggleExpandedState(button, detailsSection, isExpanded, materialItem) {
    const action = isExpanded ? 'remove' : 'add';
    const text = isExpanded ? 'View more details' : 'View less details';
    const rotation = isExpanded ? '0deg' : '180deg';

    button.classList[action]('expanded');
    detailsSection.classList[action]('expanded');

    const spanElement = button.querySelector('span');
    if (spanElement) spanElement.textContent = text;

    const chevron = button.querySelector('.chevron-down');
    if (chevron) chevron.style.transform = `rotate(${rotation})`;

    if (!isExpanded) {
      this.populateExpandedDetails(materialItem, detailsSection);
    }
  }
  _toggleExpandedStateChoice(button, detailsSection, isExpanded, materialItem) {
    const action = isExpanded ? 'remove' : 'add';
    const text = isExpanded ? 'View details' : 'Hide details';
    const rotation = isExpanded ? '0deg' : '180deg';
    const sectionScope = (button || materialItem)?.closest?.('.course-section') || document;

    button.classList[action]('expanded');
    detailsSection.classList[action]('expanded');

    const spanElement = button.querySelector('span');
    if (spanElement) spanElement.textContent = text;

    const chevron = button.querySelector('.chevron-down');
    if (chevron) chevron.style.transform = `rotate(${rotation})`;

    const idProduct = this._choiceProductIdFromToggleButton(button);
    const choiceInfo = idProduct
      ? document.getElementById('choice-details-product-' + idProduct)
      : null;

    if (choiceInfo) {
      if (!isExpanded) {
        this._portalChoiceDrawerOpen(choiceInfo);
        choiceInfo.classList.add("choice-show");
        choiceInfo.classList.remove("choice-hide");
      } else {
        choiceInfo.classList.add("choice-hide");
        choiceInfo.classList.remove("choice-show");
        this._portalChoiceDrawerClose(choiceInfo);
      }
    }
    this._syncChoiceDrawerScrollLock();
  }
  _toggleExpandedStateChoiceCheck(button, detailsSection, isExpanded, materialItem) {
    const action = isExpanded ? 'remove' : 'add';
    const sectionScope = (button || materialItem)?.closest?.('.course-section') || document;

    button.classList[action]('expanded');
    detailsSection.classList[action]('expanded');

    const idProduct = this._choiceProductIdFromToggleButton(button);
    const choiceInfo = idProduct
      ? document.getElementById('choice-details-product-' + idProduct)
      : null;

    if (!isExpanded) {
      if (choiceInfo) {
        this._portalChoiceDrawerOpen(choiceInfo);
        choiceInfo.classList.add("choice-show");
        choiceInfo.classList.remove("choice-hide");
      }
    } else {
      if (choiceInfo) {
        choiceInfo.classList.add("choice-hide");
        choiceInfo.classList.remove("choice-show");
        this._portalChoiceDrawerClose(choiceInfo);
      }

      if (idProduct) {
        this.updateViewMoreDetailsChoice(idProduct, sectionScope);

        if (!this._choiceDrawerUsesInlineLayout()) {
          const variantCheckboxes = sectionScope.querySelectorAll(
            `input[type="checkbox"][data-product-id="${idProduct}"]`
          );
          variantCheckboxes.forEach(cb => {
            if (cb.checked) {
              cb.dispatchEvent(new MouseEvent('click', { bubbles: true }));
            }
          });

          this.hideShowPriceChoice(true, '', idProduct, sectionScope);
        } else {
          const panel = document.getElementById(
            'choice-details-product-' + idProduct
          );
          let selectedCount = 0;
          let priceProductSelected = '';
          panel
            ?.querySelectorAll(
              `.purchase-checkbox[data-product-id="${idProduct}"]:checked`
            )
            .forEach(cb => {
              selectedCount += 1;
              const p = cb.getAttribute('data-variant-price');
              if (p) priceProductSelected = p;
            });
          const isAnySelected = selectedCount >= 2;
          this.hideShowPriceChoice(
            isAnySelected,
            priceProductSelected,
            idProduct,
            sectionScope
          );
          const rowCb = document.getElementById(
            'check-box-product-' + idProduct
          );
          const sid = rowCb?.getAttribute('data-section-id');
          if (sid) {
            this._syncChoiceMinimumAndExtraData(sid);
          }
        }

        const divDetails = sectionScope.querySelector(
          '#div-view-details-' + idProduct
        );
        if (divDetails) divDetails.classList.add('material-view-more-choice');
      }
    }
    this._syncChoiceDrawerScrollLock();
  }
  decodeHtmlEntities(str) {
      const txt = document.createElement("textarea"); 
      txt.innerHTML = str; 
      return txt.value; 
  }
  populateExpandedDetails(materialItem, detailsSection) {
    const titleElement = materialItem.querySelector('.material-title');
    if (!titleElement) {
      detailsSection.innerHTML = this._generateErrorHTML('No title found');
      return;
    }

    const product = window.courseData.products_Materials.find(
      p => this.decodeHtmlEntities(p.title) === titleElement.textContent.trim()
    );
    if (!product) {
      detailsSection.innerHTML = this._generateErrorHTML('Product not found');
      return;
    }

    const details = this._extractProductDetails(product);
    detailsSection.innerHTML = this._generateDetailsHTML(details);
  }

  _generateErrorHTML(message) {
    return `<div class="expanded-details-content"><div class="detail-row">Error: <span class="detail-value">${message}</span></div></div>`;
  }

  _extractProductDetails(product) {
    return {
      'Publisher':
        this.productHandler?.extractPublisher?.(product) || 'Not specified',
      'Format':
        this.productHandler?.extractFormat?.(product) || 'Not specified',
      'Copyright':
        this.productHandler?.extractCopyRight?.(product) || 'Not specified',
      'Publication':
        this.productHandler?.extractPublication?.(product) || 'Not specified'
    };
  }

  _generateDetailsHTML(details) {
    const rows = Object.entries(details)
      .map(
        ([key, value]) =>
          value === "Not specified" 
            ? ''  // Retorna uma string vazia se o valor for "Not Specficated"
            : `<div class="detail-row">${key}: <span class="detail-value">${value}</span></div>`
      )
      .join('');
    return `<div class="expanded-details-content">${rows}</div>`;
  }

  getVariantByOptions(product, format, type, condition = 'New') {
    if (!product.variants?.length) return null;

    return product.variants.find(variant => {
      const formatMatch = variant.option1
        ?.toLowerCase()
        .includes(format.toLowerCase());
      const typeMatch = this._matchPurchaseType(variant, type);
      const conditionMatch = this._matchCondition(variant, condition);
      return formatMatch && typeMatch && conditionMatch;
    });
  }

  _matchPurchaseType(variant, type) {
    if (type === 'buy') {
      return variant.option2?.toLowerCase().includes('buy');
    }
    if (type === 'rent' || type.startsWith('rent-')) {
      let typeMatch = variant.option2?.toLowerCase().includes('rent');
      if (type.includes('-')) {
        const requestedDays = type.split('-')[1];
        typeMatch = typeMatch && variant.option2?.includes(requestedDays);
      }
      return typeMatch;
    }
    return false;
  }

  _matchCondition(variant, condition) {
    if (!condition || condition === 'New') return true;
    return (
      variant.option1?.toLowerCase().includes(condition.toLowerCase()) ||
      variant.option3?.toLowerCase().includes(condition.toLowerCase())
    );
  }

  findVariantAcrossProducts(format, type, currentProduct, condition = 'New') {
    for (const product of this.products) {
      const variant = this.getVariantByOptions(
        product,
        format,
        type,
        condition
      );
      if (variant) return variant;
    }
    return null;
  }

  getSelectedFormat(materialItem) {
    const selectedBtn = materialItem.querySelector('.format-btn.selected');
    return selectedBtn ? selectedBtn.getAttribute('data-format') : null;
  }

  getInteractionStats() {
    const selectors = {
      totalMaterials: '.material-item',
      formatsSelected: '.format-btn.selected',
      itemsSelected: '.purchase-checkbox:checked',
      detailsExpanded: '.material-details-expanded.expanded',
    };

    return Object.entries(selectors).reduce((stats, [key, selector]) => {
      stats[key] = document.querySelectorAll(selector).length;
      return stats;
    }, {});
  }

  dispatchInteractionEvent(type, data = {}) {
    const event = new CustomEvent('materialInteraction:changed', {
      detail: { type, data, stats: this.getInteractionStats() },
    });
    document.dispatchEvent(event);
  }

  initialize() {
    const materialItems = document.querySelectorAll('.material-item');
    materialItems.forEach(item => {
      const titleElement = item.querySelector('.material-title');
      if (titleElement) {
        const product = this.products.find(
          p => p.title === titleElement.textContent.trim()
        );
        if (product) this.addMaterialEventListeners(item, product);
      }
    });
  }

  async _updateInBagIndicators(materialItem) {
    const cartChecker = window.cartChecker;
    if (!cartChecker) return;

    const purchaseTypes = materialItem.querySelectorAll('.purchase-type');
    const variantIds = Array.from(purchaseTypes)
      .map(pt => pt.getAttribute('data-variant-id'))
      .filter(Boolean);

    if (variantIds.length === 0) return;

    try {
      const inCartResults = await cartChecker.checkMultipleVariants(variantIds);
      purchaseTypes.forEach(purchaseType => {
        const variantId = purchaseType.getAttribute('data-variant-id');
        const inBagIndicator = purchaseType.querySelector('.in-bag-indicator');

        if (variantId && inBagIndicator) {
          const isInCart = inCartResults[variantId];
          inBagIndicator.style.display = isInCart ? 'flex' : 'none';
          purchaseType.classList.toggle('in-cart', isInCart);
        }
      });
    } catch (error) {}
  }

  async updateAllInBagIndicators() {
    const materialItems = document.querySelectorAll('.material-item');
    for (const materialItem of materialItems) {
      await this._updateInBagIndicators(materialItem);
    }
  }

  destroy() {
    ['cart:add', 'cart:update', 'cart:change'].forEach(event =>
      document.removeEventListener(event, this.handleCartUpdate)
    );

    if (typeof window !== 'undefined') {
      delete window.courseMaterialsInteractionManager;
    }
  }
}

window.MaterialInteractionManager = MaterialInteractionManager;

window.togglePurchaseOption = function (purchaseTypeDiv, event) {
  window.courseMaterialsInteractionManager?.togglePurchaseOption(
    purchaseTypeDiv,
    event
  );
};

window.handlePurchaseSelection = function (checkbox) {
  window.courseMaterialsInteractionManager?.handlePurchaseSelection(checkbox);
};

window.addMaterialEventListeners = function (materialItem, product) {
  window.courseMaterialsInteractionManager?.addMaterialEventListeners(
    materialItem,
    product
  );
};

window.handleNotifyMe = function (variantId, productTitle, format) {
  if (typeof window.openNotifyMeDialog === 'function') {
    window.openNotifyMeDialog(variantId, productTitle, format);
  } else {
    console.warn(
      'openNotifyMeDialog function not found. Make sure notify-me-dialog component is loaded.'
    );
  }
};

window.toggleFormatCollapse = function (button) {
  
  const container = button.closest('.purchase-options-container');
  if (!container) return;
  
  const format = button.dataset.format;
  
  // Remove active class from all tabs
  const allTabs = container.querySelectorAll('.format-tab');
  allTabs.forEach(tab => tab.classList.remove('active'));
  
  // Add active class to clicked tab
  button.classList.add('active');
  
  // Hide all format collapses
  const allCollapses = container.querySelectorAll('.format-collapse');
  allCollapses.forEach(collapse => {
    collapse.style.display = 'none';
  });
  
  // Show the selected format collapse
  const targetCollapse = container.querySelector(`.format-collapse[data-format="${format}"]`);
  if (targetCollapse) {
    targetCollapse.style.display = 'block';
    const targetContent = targetCollapse.querySelector('.collapse-content');
    if (targetContent) {
      syncCmrpVariantNoticeInCollapse(targetContent);
    }
  }
};